import { useState, useEffect } from 'react';
import SidebarNav from '../../components/MasterSideBar';
import TopbarNav from '../../components/TopbarNav';
import TimesheetApproverView from '../../components/TimesheetApproverView'; 
import TimesheetFormModal from '../../components/Timesheetform'; 
import TimesheetTable from '../../components/TimesheetTable';
import { fetchAllPendingTimesheets, fetchAllTimesheets, verifyTimesheetAction } from "../../api/Timesheetform-api"; 
import { FiShield, FiLoader, FiPlus } from 'react-icons/fi';
import { toast } from 'react-toastify';

const ApproverTimesheetPage = () => {
  const [timesheets, setTimesheets] = useState([]);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState("pending");
  
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    setCurrentUser(storedUser);
  }, []);

  // Reload whenever the active tab changes
  useEffect(() => {
    loadData();
  }, [activeTab]);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/login";
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const data = activeTab === "pending"
        ? await fetchAllPendingTimesheets(token)
        : await fetchAllTimesheets(token);
      setTimesheets(Array.isArray(data) ? data : data.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load timesheets.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenView = (id) => {
    const found = timesheets.find(ts => ts._id === id);
    if (found) {
      setSelectedTimesheet(found);
      setIsViewOpen(true);
    }
  };

  const handleStatusUpdate = async (id, status, reason = "") => {
    if (!id) {
      toast.error("Invalid Timesheet ID");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await verifyTimesheetAction(id, status, reason, token);
      toast.success(`Timesheet ${status} successfully!`);
      setIsViewOpen(false);
      loadData(); 
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Action failed";
      toast.error(errorMsg);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex overflow-x-hidden">
      <div className="z-[999]">
        <SidebarNav 
          visible={sidebarOpen} 
          onClose={() => setSidebarOpen(false)} 
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          onLogout={handleLogout} 
        />
      </div>
      
      <div className={`flex-1 flex flex-col transition-all duration-300 ${collapsed ? "md:ml-20" : "md:ml-64"}`}> 
        <TopbarNav onSidebarToggle={() => setSidebarOpen(true)} />

        <main className="p-4 md:p-8 pt-20 md:pt-24 relative min-h-screen">
          <header className="mb-6 md:mb-10">
            <div>
              <h1 className="text-2xl md:text-4xl font-black text-gray-800 uppercase tracking-tighter flex items-center gap-3">
                <FiShield className="text-orange-600" /> Approvals
              </h1>
              <p className="text-gray-400 text-sm font-medium">Review and verify staff logs.</p>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mt-6">
              {[
                { key: "pending", label: "Pending Approval" },
                { key: "all", label: "All Timesheets" },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-5 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-colors ${
                    activeTab === tab.key
                      ? "bg-orange-600 text-white shadow-md shadow-orange-100"
                      : "bg-white text-gray-400 border border-gray-200 hover:border-orange-300 hover:text-orange-500"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <button
              onClick={() => setIsFormOpen(true)}
              className="hidden md:flex absolute top-24 right-8 items-center gap-2 px-8 py-4 bg-orange-600 text-white rounded-2xl hover:bg-orange-700 shadow-xl shadow-orange-100 transition-all font-bold uppercase text-xs tracking-widest"
            >
              <FiPlus size={20} /> New Entry
            </button>
          </header>

          <section>
            <TimesheetTable 
              timesheets={timesheets} 
              onViewTimesheet={handleOpenView}
              loading={loading}
            />
          </section>

          <button
            onClick={() => setIsFormOpen(true)}
            className="md:hidden fixed bottom-6 right-6 w-14 h-14 bg-orange-600 text-white rounded-full shadow-2xl flex items-center justify-center z-50 active:scale-90 transition-transform"
            aria-label="New Entry"
          >
            <FiPlus size={28} />
          </button>
        </main>
      </div>

      {isViewOpen && (
        <TimesheetApproverView 
          timesheet={selectedTimesheet} 
          onClose={() => setIsViewOpen(false)}
          isApprover={true}
          onAction={handleStatusUpdate}
          currentUserId={currentUser?._id || currentUser?.id}
        />
      )}

      <TimesheetFormModal 
        isOpen={isFormOpen} 
        onClose={() => setIsFormOpen(false)}
        onSubmitSuccess={() => {
          setIsFormOpen(false);
          loadData(); 
          toast.success("Your timesheet has been submitted.");
        }}
      />
    </div>
  );
};

export default ApproverTimesheetPage;